import { DialogFileInfo, DialogImageInfo } from "../../types/DialogChat";
import TypeDefaultResponse from "../../types/TypeDefaultResponse";
import { formatFileSize } from "../../utils/messageChat";
import {
  isTextChatAudioAttachment,
  rejectAudioTextChatAttachments,
} from "../../components/TextChat/TextChat.attachments";

export type DialogChatAttachmentResourceStatus =
  | "idle"
  | "loading"
  | "ready"
  | "failed";

export type DialogChatAttachmentResourceKind = "image" | "file";

export type DialogChatAttachmentResource = {
  id: string;
  messageId: string;
  kind: DialogChatAttachmentResourceKind;
  status: DialogChatAttachmentResourceStatus;
  type: string;
  source: string;
  filename: string;
  size?: string;
  canDownload: boolean;
};

export type DialogChatAttachmentSnapshot = {
  messageId: string;
  status: DialogChatAttachmentResourceStatus;
  resources: DialogChatAttachmentResource[];
  error?: string;
};

type DialogChatAttachmentResourceEntry = DialogChatAttachmentSnapshot & {
  promise?: Promise<DialogChatAttachmentSnapshot>;
  failedAt?: number;
  objectUrls: string[];
};

export type DialogChatAttachmentResourceApi = {
  getMessageImages: (
    token: string,
    messageId: string,
  ) => Promise<TypeDefaultResponse & { images?: DialogImageInfo[] }>;
  getMessageFiles: (
    token: string,
    messageId: string,
  ) => Promise<TypeDefaultResponse & { files?: DialogFileInfo[] }>;
  getMessageFileStream: (fileId: string) => Promise<Blob>;
};

const FAILED_CACHE_TTL_MS = 10_000;
export const isDialogChatAudioAttachment = isTextChatAudioAttachment;
export const rejectAudioDialogChatAttachments = rejectAudioTextChatAttachments;

const toFileExtension = (filename: string, fallbackType: string) =>
  filename.split(".").pop() || fallbackType || "file";

const createFailedResource = (
  messageId: string,
  kind: DialogChatAttachmentResourceKind,
  index: number,
): DialogChatAttachmentResource => ({
  id: `${messageId}-${kind}-failed-${index}`,
  messageId,
  kind,
  status: "failed",
  type: "file",
  source: "",
  filename: kind === "image" ? "Image" : "Attachment",
  canDownload: false,
});

const mapImage = (
  messageId: string,
  image: DialogImageInfo,
  index: number,
): DialogChatAttachmentResource => ({
  id: image._id || image.link || `${messageId}-image-${index}`,
  messageId,
  kind: "image",
  status: "ready",
  type: image.type || toFileExtension(image.filename, "image"),
  source: image.link,
  filename: image.filename,
  size: formatFileSize(image.size),
  canDownload: false,
});

const mapFile = async (
  api: DialogChatAttachmentResourceApi,
  messageId: string,
  file: DialogFileInfo,
  index: number,
) => {
  if (file.link) {
    return {
      resource: {
        id: file._id || file.link || `${messageId}-file-${index}`,
        messageId,
        kind: "file",
        status: "ready",
        type: file.type || toFileExtension(file.filename, "file"),
        source: file.link,
        filename: file.filename,
        size: formatFileSize(file.size),
        canDownload: true,
      } satisfies DialogChatAttachmentResource,
      objectUrl: undefined,
    };
  }

  if (!file._id) {
    return {
      resource: createFailedResource(messageId, "file", index),
      objectUrl: undefined,
    };
  }

  const blob = await api.getMessageFileStream(file._id);
  const objectUrl = URL.createObjectURL(blob);

  return {
    resource: {
      id: file._id,
      messageId,
      kind: "file",
      status: "ready",
      type: file.type || toFileExtension(file.filename, "file"),
      source: objectUrl,
      filename: file.filename,
      size: formatFileSize(file.size),
      canDownload: true,
    } satisfies DialogChatAttachmentResource,
    objectUrl,
  };
};

const cloneSnapshot = (
  entry: DialogChatAttachmentResourceEntry,
): DialogChatAttachmentSnapshot => ({
  messageId: entry.messageId,
  status: entry.status,
  resources: entry.resources,
  error: entry.error,
});

export const createDialogChatAttachmentResourceCache = (
  api: DialogChatAttachmentResourceApi,
) => {
  const entries = new Map<string, DialogChatAttachmentResourceEntry>();

  const revokeEntryObjectUrls = (entry?: DialogChatAttachmentResourceEntry) => {
    entry?.objectUrls.forEach((source) => URL.revokeObjectURL(source));
    if (entry) {
      entry.objectUrls = [];
    }
  };

  const load = async (
    token: string,
    messageId: string,
  ): Promise<DialogChatAttachmentSnapshot> => {
    const existing = entries.get(messageId);
    if (existing?.status === "ready") {
      return cloneSnapshot(existing);
    }

    if (
      existing?.status === "failed" &&
      existing.failedAt &&
      Date.now() - existing.failedAt < FAILED_CACHE_TTL_MS
    ) {
      return cloneSnapshot(existing);
    }

    if (existing?.promise) {
      return existing.promise;
    }

    const loadingEntry: DialogChatAttachmentResourceEntry = {
      messageId,
      status: "loading",
      resources: [],
      objectUrls: existing?.objectUrls || [],
    };

    entries.set(messageId, loadingEntry);

    const promise = Promise.all([
      api.getMessageImages(token, messageId),
      api.getMessageFiles(token, messageId),
    ])
      .then(async ([imagesResponse, filesResponse]) => {
        revokeEntryObjectUrls(loadingEntry);

        if (!imagesResponse.status || !filesResponse.status) {
          throw new Error("Attachment resources are unavailable");
        }

        const imageResources = (imagesResponse.images || []).map((image, index) =>
          mapImage(messageId, image, index),
        );
        const mappedFiles = await Promise.all(
          (filesResponse.files || []).map((file, index) =>
            mapFile(api, messageId, file, index),
          ),
        );
        const fileResources = mappedFiles.map((item) => item.resource);
        const objectUrls = mappedFiles
          .map((item) => item.objectUrl)
          .filter(Boolean) as string[];
        const readyEntry: DialogChatAttachmentResourceEntry = {
          messageId,
          status: "ready",
          resources: [...imageResources, ...fileResources],
          objectUrls,
        };

        entries.set(messageId, readyEntry);
        return cloneSnapshot(readyEntry);
      })
      .catch((error) => {
        revokeEntryObjectUrls(loadingEntry);
        const failedEntry: DialogChatAttachmentResourceEntry = {
          messageId,
          status: "failed",
          resources: [createFailedResource(messageId, "file", 0)],
          error: error instanceof Error ? error.message : "Attachment failed",
          failedAt: Date.now(),
          objectUrls: [],
        };

        entries.set(messageId, failedEntry);
        return cloneSnapshot(failedEntry);
      });

    loadingEntry.promise = promise;
    return promise;
  };

  return {
    getSnapshot(messageId: string): DialogChatAttachmentSnapshot {
      const entry = entries.get(messageId);
      return entry
        ? cloneSnapshot(entry)
        : {
            messageId,
            status: "idle",
            resources: [],
          };
    },
    load,
    retry(token: string, messageId: string) {
      const entry = entries.get(messageId);
      revokeEntryObjectUrls(entry);
      entries.delete(messageId);
      return load(token, messageId);
    },
    clear(messageId?: string) {
      if (messageId) {
        revokeEntryObjectUrls(entries.get(messageId));
        entries.delete(messageId);
        return;
      }

      entries.forEach((entry) => revokeEntryObjectUrls(entry));
      entries.clear();
    },
  };
};
